import { useState } from 'react';
import { useToast } from '@crisellevaldez/speculo-ui';
import {
  getSpeculoOAuthToken,
  refreshSpeculoOAuthToken,
  getOAuthClientInfo,
  pollOAuthToken,
  type OAuthClientInfo,
  type SpeculoOAuthTokenResponse,
} from '../../api/speculoOAuth';

export const useSpeculoOAuthOperations = () => {
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFetchingInfo, setIsFetchingInfo] = useState(false);
  const [clientInfo, setClientInfo] = useState<OAuthClientInfo | null>(null);

  const { showToast } = useToast();

  /**
   * Create or get existing OAuth client.
   * The endpoint may return queued:true (async Pub/Sub path) — in that case
   * we poll GET /oauth/token until the downstream consumer writes the client.
   */
  const createOAuthClient = async (orgId?: string) => {
    setIsCreatingClient(true);
    try {
      const result = await getSpeculoOAuthToken(orgId);

      // Async / queued path — poll until the client is ready
      if ('queued' in result && result.queued) {
        showToast({ message: 'OAuth client queued — waiting for confirmation…', variant: 'info' });
        const polled = await pollOAuthToken(orgId);
        if (!polled?.clientId) {
          showToast({ message: 'OAuth client creation is taking longer than expected. Check back shortly.', variant: 'error' });
          return null;
        }
        const newClientInfo = { clientId: polled.clientId, scope: polled.scope, scopes: polled.scopes, existing: polled.existing || false };
        setClientInfo(newClientInfo);
        showToast({ message: 'OAuth client created successfully', variant: 'success' });
        return polled;
      }

      // Synchronous path (existing behaviour)
      const syncResult = result as SpeculoOAuthTokenResponse;
      const newClientInfo = {
        clientId: syncResult.clientId,
        scope: syncResult.scope,
        scopes: syncResult.scopes,
        existing: syncResult.existing || false,
      };
      setClientInfo(newClientInfo);

      showToast({
        message: syncResult.existing ? 'OAuth client verified and synced to organization' : 'OAuth client created successfully',
        variant: 'success',
      });

      return syncResult;
    } catch (error) {
      console.error('Error creating OAuth client:', error);
      const e = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
      const errorMessage = e?.response?.data?.error?.message || e?.message || 'Failed to create OAuth client';

      showToast({
        message: errorMessage,
        variant: 'error',
      });
      return null;
    } finally {
      setIsCreatingClient(false);
    }
  };

  /**
   * Refresh OAuth token
   */
  const refreshOAuthToken = async (orgId?: string) => {
    setIsRefreshing(true);
    try {
      const result = await refreshSpeculoOAuthToken(orgId);

      showToast({
        message: 'OAuth token refreshed successfully',
        variant: 'success',
      });

      return result;
    } catch (error) {
      console.error('Error refreshing OAuth token:', error);
      const e = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
      const errorMessage = e?.response?.data?.error?.message || e?.message || 'Failed to refresh OAuth token';

      showToast({
        message: errorMessage,
        variant: 'error',
      });
      return null;
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Fetch OAuth client information
   */
  const fetchClientInfo = async (orgId?: string) => {
    setIsFetchingInfo(true);
    try {
      const info = await getOAuthClientInfo(orgId);
      setClientInfo(info);
      return info;
    } catch (error) {
      console.error('Error fetching OAuth client info:', error);
      const e = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
      const errorMessage = e?.response?.data?.error?.message || e?.message || 'Failed to fetch OAuth client info';

      showToast({
        message: errorMessage,
        variant: 'error',
      });
      return null;
    } finally {
      setIsFetchingInfo(false);
    }
  };

  return {
    // State
    clientInfo,

    // Operations
    createOAuthClient,
    refreshOAuthToken,
    fetchClientInfo,

    // Loading states
    isCreatingClient,
    isRefreshing,
    isFetchingInfo,
  };
};
